import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
const { FiClipboard, FiPlus, FiX, FiCornerDownLeft, FiSmartphone } = FiIcons;
import SafeIcon from '../common/SafeIcon';

export default function ClipboardCaptureUI({ data, deviceClass, onCreate, onDismiss }) {
  const [progress, setProgress] = useState(100);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);
  const duration = 12000;

  const isDesktop = deviceClass === 'desktop';

  useEffect(() => {
    if (!data || !isDesktop) return;
    const startTimer = () => {
      timerRef.current = setInterval(() => {
        if (!isPaused) {
          setProgress((prev) => {
            const next = prev - (100 / (duration / 100));
            if (next <= 0) {
              clearInterval(timerRef.current);
              onDismiss();
              return 0;
            }
            return next;
          });
        }
      }, 100);
    };
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [data, isPaused, onDismiss, isDesktop]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Enter') { e.preventDefault(); onCreate(); }
      else if (e.key === 'Escape') { e.preventDefault(); onDismiss(); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onCreate, onDismiss]);

  if (!data) return null;

  // --- MOBILE BOTTOM SHEET ---
  if (!isDesktop) {
    return (
      <div className="fixed inset-0 z-[120] flex items-end justify-center bg-black/60 backdrop-blur-sm pointer-events-auto">
        <motion.div 
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          className="w-full bg-gray-900 rounded-t-3xl border-t border-indigo-500/30 p-6 pb-10 shadow-2xl"
        >
          <div className="w-12 h-1.5 bg-gray-700 rounded-full mx-auto mb-6" />
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-indigo-500/10 text-indigo-400 rounded-2xl">
              <SafeIcon icon={FiClipboard} className="text-2xl" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Clipboard Content</h3>
              <p className="text-xs text-gray-400">Would you like to save this as a snippet?</p>
            </div>
          </div>
          <div className="bg-gray-950 rounded-2xl p-4 mb-8 border border-gray-800 max-h-48 overflow-y-auto">
            <p className="text-sm text-gray-300 font-mono whitespace-pre-wrap">{data.preview}</p>
          </div>
          <div className="flex flex-col gap-3">
            <button onClick={onCreate} className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-2xl font-bold flex items-center justify-center gap-2">
              <SafeIcon icon={FiPlus} /> Create Snippet
            </button>
            <button onClick={onDismiss} className="w-full py-4 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-2xl font-bold">
              Dismiss
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // --- DESKTOP BANNER ---
  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      className="fixed bottom-24 right-6 z-[80] w-full max-w-sm"
    >
      <div className="bg-gray-900 border border-indigo-500/30 rounded-xl shadow-2xl overflow-hidden ring-1 ring-white/10">
        <div className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-indigo-500/10 text-indigo-400 rounded-lg">
              <SafeIcon icon={FiClipboard} />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-bold text-white">Clipboard Detected</h4>
              <p className="text-[10px] text-gray-500 uppercase font-bold">New Card Suggestion</p>
            </div>
            <button onClick={onDismiss} className="p-1 hover:bg-gray-800 rounded text-gray-500 hover:text-white"><SafeIcon icon={FiX} /></button>
          </div>
          <div className="bg-gray-950/50 rounded-lg p-3 mb-4 border border-gray-800">
            <p className="text-xs text-gray-400 line-clamp-3 font-mono">{data.preview}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={onCreate} className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-2">
              <SafeIcon icon={FiPlus} /> Create Card
              <span className="opacity-50 text-[9px] bg-black/20 px-1 rounded flex items-center gap-1"><SafeIcon icon={FiCornerDownLeft} className="text-[8px]" /> ENTER</span>
            </button>
            <button onClick={onDismiss} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-xs font-bold">Dismiss</button>
          </div>
        </div>
        <div className="h-1 bg-gray-800 w-full overflow-hidden">
          <motion.div className="h-full bg-indigo-500" initial={{ width: "100%" }} animate={{ width: `${progress}%` }} transition={{ duration: 0.1 }} />
        </div>
      </div>
    </motion.div>
  );
}